import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, IconButton, Paper, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SupplierBillService } from '../../../services/supplierBill.service';
import { SupplierService } from '../../../services/supplier.service';
import { RepairBillService } from '../../../services/maintenance.service';
import { RepairBillDTO } from '../../../interfaces/maintenance';
import { AccountService } from '../../../services/account.service';
import { SegmentService } from '../../../services/segment.service';
import { AuthService } from '../../../services/auth.service';
import { Supplier } from '../../../interfaces/procurement/supplier.interface';
import { Account, Segment } from '../../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../../common/AccountAutocomplete';
import SearchableSelect from '../../common/SearchableSelect';
import TaxCodeSelect from '../../tax/TaxCodeSelect';
import { hasTaxCodes, useTaxCodeLookup } from '../../tax/useTaxCodeLookup';
import { leafAccounts } from '../../../utils/postingAccounts';
import { useToast } from '../../../context/ToastContext';
import { formatMoney } from '../../../utils/format';
import DocumentCurrencyFields from '../../common/DocumentCurrencyFields';
import { isForeignCurrency } from '../../../utils/fcDocuments';
import { toLocalIsoDate } from '../../../utils/dateUtils';

interface LineState { accountId: string; segmentId: string; description: string; amount: string; taxCodeId: string | null; }
const emptyLine = (): LineState => ({ accountId: '', segmentId: '', description: '', amount: '', taxCodeId: null });
const plus30 = () => { const d = new Date(); d.setDate(d.getDate() + 30); return toLocalIsoDate(d); };

/**
 * W3-F: a supplier bill with no purchase order or goods receipt, whose lines debit GL accounts directly. Use it for a
 * C&F agent's bill (debit the import clearing account) so a bill of entry can then charge against it.
 */
export const SupplierServiceBillCreate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const repairBillId = searchParams.get('repairBill');
  const [repairBill, setRepairBill] = useState<RepairBillDTO | null>(null);
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution() ?? '';
  const taxLookup = useTaxCodeLookup(institutionId);
  const showTaxCodes = hasTaxCodes(taxLookup);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [billDate, setBillDate] = useState(toLocalIsoDate());
  const [dueDate, setDueDate] = useState(plus30());
  const [lines, setLines] = useState<LineState[]>([emptyLine()]);
  // ---- B4-C: document currency ----
  const [currency, setCurrency] = useState('BDT');
  const [fxRate, setFxRate] = useState<number | ''>('');
  const foreign = isForeignCurrency(currency);
  // ---- end B4-C ----

  useEffect(() => {
    const init = async () => {
      try {
        const [sup, accs] = await Promise.all([SupplierService.listByInstitution(0, 200), AccountService.getAllAccounts()]);
        setSuppliers(sup.content);
        setAccounts(accs ?? []);
        if (repairBillId) {
          // Maintenance sent this repair bill for approval: pre-fill the form from it.
          try {
            const rb = await RepairBillService.get(repairBillId);
            setRepairBill(rb);
            if (rb.supplierId) setSupplierId(rb.supplierId);
            if (rb.billRef) setInvoiceRef(rb.billRef);
            if (rb.billDate) setBillDate(rb.billDate);
            const repairsAcc = leafAccounts(accs ?? []).find(a => /repairs?\s*(&|and)\s*maintenance/i.test(a.name));
            const asset = rb.fixedAssetCode ? `, fixed asset ${rb.fixedAssetCode}${rb.fixedAssetName ? ` ${rb.fixedAssetName}` : ''}` : '';
            const equip = rb.equipmentCode ? `, equipment ${rb.equipmentCode}${rb.equipmentName ? ` ${rb.equipmentName}` : ''}` : '';
            setLines([{ accountId: repairsAcc ? String(repairsAcc.id) : '', segmentId: '',
              description: `Repair bill for work order ${rb.woNo}${equip}${asset}`.slice(0, 250),
              amount: String(rb.amount), taxCodeId: null }]);
          } catch (e) {
            toast.showApiError(e, 'Failed to load the repair bill');
          }
        }
        SegmentService.getByInstitution(0, 500)
          .then(res => setSegments((res.content as Segment[]).filter(s => s.active !== false)))
          .catch(() => setSegments([]));
      } catch (e) {
        toast.showApiError(e, 'Failed to load suppliers and accounts');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const postingAccounts = useMemo(() => leafAccounts(accounts), [accounts]);
  const setLine = (i: number, patch: Partial<LineState>) => setLines(ls => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const subtotal = lines.reduce((a, l) => a + (Number(l.amount) > 0 ? Number(l.amount) : 0), 0);
  const valid = supplierId !== '' && billDate !== '' && dueDate !== '' && lines.length > 0
    && lines.every(l => l.accountId !== '' && Number(l.amount) > 0)
    && (!foreign || Number(fxRate) > 0);

  const save = async () => {
    try {
      setSaving(true);
      const dto = {
        institutionId, supplierId, supplierInvoiceRef: invoiceRef || undefined, billDate, dueDate,
        ...(foreign ? { currencyCode: currency, exchangeRate: Number(fxRate) } : {}),
        lines: lines.map(l => ({ accountId: l.accountId, segmentId: l.segmentId || null,
          description: l.description || undefined, amount: Number(l.amount), taxCodeId: l.taxCodeId })),
      };
      if (repairBill) {
        // One server call: posts the bill and approves the repair bill in one transaction, so a failure
        // leaves nothing posted and a retry cannot book the expense twice.
        const approved = await RepairBillService.approveWithBill(repairBill.id, dto);
        toast.showSuccess('Service bill posted and repair bill approved');
        if (approved.warning) toast.showWarning(approved.warning);
        navigate(`/procurement/supplier-bills/${approved.supplierBillId}`);
        return;
      }
      const bill = await SupplierBillService.createServiceBill(dto);
      toast.showSuccess('Service bill posted');
      navigate(`/procurement/supplier-bills/${bill.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to post service bill');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-page="procurement.svcBillForm.page">
      <Typography variant="h5">New Service Bill</Typography>
      <Alert severity="info">
        A bill without a purchase order: each line debits the account you choose and the total is owed to the supplier.
        For a C&F agent&apos;s bill, debit the import clearing account, then charge it from the bill of entry.
      </Alert>
      {repairBill && (
        <Alert severity="info" data-help="procurement.svcBillForm.repairBill">
          Repair bill from maintenance for work order {repairBill.woNo}
          {repairBill.fixedAssetCode ? `, fixed asset ${repairBill.fixedAssetCode}` : ''}.
          {!repairBill.supplierId && repairBill.vendorName ? ` The vendor was typed as "${repairBill.vendorName}", so pick the matching supplier or create it first.` : ''}
          {' '}Posting this bill approves the repair bill. It is not added to the asset cost.
        </Alert>
      )}
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="procurement.svcBillForm.section.main">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: '1 1 260px', maxWidth: 380 }} data-help="procurement.svcBillForm.field.supplier">
            <SearchableSelect<Supplier> options={suppliers} value={supplierId} label="Supplier" required
              getOptionLabel={s => s.name} onChange={id => setSupplierId(id)} />
          </Box>
          <TextField data-help="procurement.svcBillForm.field.invoiceRef" label="Supplier invoice ref" value={invoiceRef} onChange={e => setInvoiceRef(e.target.value)} sx={{ flex: '1 1 200px', maxWidth: 280 }} />
          <TextField data-help="procurement.svcBillForm.field.billDate" label="Bill date" type="date" value={billDate} onChange={e => setBillDate(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
          <TextField data-help="procurement.svcBillForm.field.dueDate" label="Due date" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
        </Box>
        <DocumentCurrencyFields institutionId={institutionId} currency={currency} rate={fxRate} asOf={billDate}
          onChange={(c, r) => { setCurrency(c); setFxRate(r); }} />
        {lines.map((l, i) => (
          <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flex: '1 1 260px', maxWidth: 360 }} data-help="procurement.svcBillForm.field.account">
              <AccountAutocomplete accounts={postingAccounts} value={l.accountId} label="Debit account" required
                onChange={v => setLine(i, { accountId: v })} />
            </Box>
            {segments.length > 0 && (
              <Box sx={{ flex: '1 1 180px', maxWidth: 240 }} data-help="procurement.svcBillForm.field.segment">
                <SearchableSelect<Segment> options={segments} value={l.segmentId} label="Segment"
                  getOptionLabel={s => `${s.code} - ${s.name}`} onChange={v => setLine(i, { segmentId: v })} />
              </Box>
            )}
            <TextField data-help="procurement.svcBillForm.field.description" label="Description" value={l.description} onChange={e => setLine(i, { description: e.target.value })}
              sx={{ flex: '1 1 200px', maxWidth: 320 }} />
            <TextField data-help="procurement.svcBillForm.field.amount" label={foreign ? `Amount (${currency})` : 'Amount'} type="number" value={l.amount} onChange={e => setLine(i, { amount: e.target.value })}
              sx={{ flex: '1 1 140px', maxWidth: 180 }} />
            {showTaxCodes && (
              <TaxCodeSelect lookup={taxLookup} side="INPUT" value={l.taxCodeId} onChange={id => setLine(i, { taxCodeId: id })} />
            )}
            <IconButton data-help="procurement.svcBillForm.removeLine" aria-label="Remove line" disabled={lines.length === 1}
              onClick={() => setLines(ls => ls.filter((_x, k) => k !== i))}><DeleteIcon /></IconButton>
          </Box>
        ))}
        <Box><Button data-help="procurement.svcBillForm.addLine" startIcon={<AddIcon />} onClick={() => setLines(ls => [...ls, emptyLine()])}>Add line</Button></Box>
        <Typography>Subtotal: {foreign ? `${currency} ${subtotal.toFixed(2)}${Number(fxRate) > 0 ? ` = BDT ${formatMoney(subtotal * Number(fxRate))}` : ''}` : formatMoney(subtotal)} (tax is added from the tax codes)</Typography>
      </Paper>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button data-help="procurement.svcBillForm.cancel" onClick={() => navigate('/procurement/supplier-bills')}>Cancel</Button>
        <Button data-help="procurement.svcBillForm.save" variant="contained" disabled={!valid || saving} onClick={save}>Post bill</Button>
      </Box>
    </Box>
  );
};

export default SupplierServiceBillCreate;
